import { RaasPortalId } from "../../../types";
import { Dropdown, Icon, Skeleton, Tooltip, type TableProps } from "@findemdev/maui";
import { decodeString } from "@findemdev/findem-helpers";
import { useContext, useMemo } from "react";
import UserContext from "../../../../../contexts/UserContext";
import { getRaasProjectCollaboration } from "../../../collaboration";
import type { IGetCompanyLogosResponseDataObj } from "../../../../../types/pub";
import { CompanyCellContent } from "../../CompanyIdentity/CompanyCellContent";
import { PROJECT_TABLE_COLUMN_DEFINITIONS } from "../../../constants";
import {
  ActionCell,
  CellText,
  IconButton,
  MenuItemContent,
  PipelineSkeletonCell,
  StatusChip,
} from "../../../RaasClientPortal.styled";
import type {
  ISortConfig,
  IProjectRow,
  IProjectTableColumnDefinition,
  ProjectTableColumnKey,
  SortableColumnKey,
  SortOrder,
} from "../../../types";
import {
  ProjectHeaderCell,
  ProjectNameCell,
  SortableHeaderCell,
} from "../ProjectTableCells";

type ProjectTableColumns = NonNullable<TableProps<IProjectRow>["columns"]>;
type ProjectTableColumn = ProjectTableColumns[number];

interface IUseProjectTableColumnsArgs {
  portalId?: RaasPortalId;
  columnKeys: readonly ProjectTableColumnKey[];
  companyDetailsByMetaRef: Record<
    string,
    IGetCompanyLogosResponseDataObj | undefined
  >;
  onDeleteProject: ({ project }: { project: IProjectRow["project"] }) => void;
  onEditProject: ({ project }: { project: IProjectRow["project"] }) => void;
  onShareProject: ({ project }: { project: IProjectRow["project"] }) => void;
  onSort: (field: SortableColumnKey, order: SortOrder | null) => void;
  sortConfig: ISortConfig | null;
}

const renderHeader = ({
  column,
}: {
  column: IProjectTableColumnDefinition;
}) => <ProjectHeaderCell column={column} />;

const ActingUserCellContent = ({ row }: { row: IProjectRow }) => {
  if (!row.actingUser) {
    return (
      <CellText $muted={row.actingUserLabel === "-"}>
        {row.actingUserLabel}
      </CellText>
    );
  }

  const name = decodeString(row.actingUser.name || row.actingUserLabel);
  const email = row.actingUserEmailLabel;
  const tooltipTitle = email ? `${name} (${email})` : name;

  return (
    <Tooltip title={tooltipTitle}>
      <CellText>{name}</CellText>
    </Tooltip>
  );
};

const PipelineCellContent = ({ row }: { row: IProjectRow }) => {
  if (row.isPipelineLoading) {
    return (
      <PipelineSkeletonCell
        aria-label="Loading pipeline count"
        data-testid="pipeline-count-skeleton"
        role="status"
      >
        <Skeleton.Input active block size="small" />
      </PipelineSkeletonCell>
    );
  }

  return (
    <CellText $muted={row.pipelineLabel === "—"}>
      {row.pipelineLabel}
    </CellText>
  );
};

export const useProjectTableColumns = ({
  columnKeys,
  portalId,
  companyDetailsByMetaRef,
  onDeleteProject,
  onEditProject,
  onShareProject,
  onSort,
  sortConfig,
}: IUseProjectTableColumnsArgs): ProjectTableColumns => {
  const currentUser = useContext(UserContext).getUser?.();
  return useMemo<ProjectTableColumns>(
    () => {
      const columnsByKey: Record<ProjectTableColumnKey, ProjectTableColumn> = {
        project: {
          dataIndex: "projectNameLabel",
          ellipsis: true,
          key: "project",
          title: (
            <SortableHeaderCell
              columnKey="project"
              label={PROJECT_TABLE_COLUMN_DEFINITIONS.project.label}
              onSort={onSort}
              sortConfig={sortConfig}
            />
          ),
          width: PROJECT_TABLE_COLUMN_DEFINITIONS.project.width,
          render: (_value, row) => <ProjectNameCell row={row} />,
        },
        client: {
          dataIndex: "clientLabel",
          ellipsis: true,
          key: "client",
          title: renderHeader({
            column: PROJECT_TABLE_COLUMN_DEFINITIONS.client,
          }),
          width: PROJECT_TABLE_COLUMN_DEFINITIONS.client.width,
          render: (_value, row) => (
            <CompanyCellContent
              companyDetailsByMetaRef={companyDetailsByMetaRef}
              label={row.clientLabel}
              metaRef={row.clientCompanyMetaRef}
            />
          ),
        },
        acting_user_id: {
          dataIndex: "actingUserLabel",
          ellipsis: true,
          key: "acting_user_id",
          title: renderHeader({
            column: PROJECT_TABLE_COLUMN_DEFINITIONS.acting_user_id,
          }),
          width: PROJECT_TABLE_COLUMN_DEFINITIONS.acting_user_id.width,
          render: (_value, row) => <ActingUserCellContent row={row} />,
        },
        hiring_for: {
          dataIndex: "hiringForLabel",
          ellipsis: true,
          key: "hiring_for",
          title: renderHeader({
            column: PROJECT_TABLE_COLUMN_DEFINITIONS.hiring_for,
          }),
          width: PROJECT_TABLE_COLUMN_DEFINITIONS.hiring_for.width,
          render: (_value, row) => (
            <CompanyCellContent
              companyDetailsByMetaRef={companyDetailsByMetaRef}
              label={row.hiringForLabel}
              metaRef={row.hiringForCompanyMetaRef}
            />
          ),
        },
        milestone: {
          dataIndex: "milestoneLabel",
          ellipsis: true,
          key: "milestone",
          title: renderHeader({
            column: PROJECT_TABLE_COLUMN_DEFINITIONS.milestone,
          }),
          width: PROJECT_TABLE_COLUMN_DEFINITIONS.milestone.width,
          render: (_value, row) => (
            <Tooltip title={row.milestoneLabel}>
              <CellText>{row.milestoneLabel}</CellText>
            </Tooltip>
          ),
        },
        status: {
          dataIndex: ["status", "label"],
          ellipsis: true,
          key: "status",
          title: (
            <SortableHeaderCell
              columnKey="status"
              label={PROJECT_TABLE_COLUMN_DEFINITIONS.status.label}
              onSort={onSort}
              sortConfig={sortConfig}
            />
          ),
          width: PROJECT_TABLE_COLUMN_DEFINITIONS.status.width,
          render: (_value, row) => (
            <StatusChip $tone={row.status.tone} $muted={row.status.muted}>
              {row.status.status === "completed" && <Icon icon="lock" $size={12} />}
              {row.status.label}
            </StatusChip>
          ),
        },
        pipeline: {
          dataIndex: "pipelineLabel",
          ellipsis: true,
          key: "pipeline",
          title: renderHeader({
            column: PROJECT_TABLE_COLUMN_DEFINITIONS.pipeline,
          }),
          width: PROJECT_TABLE_COLUMN_DEFINITIONS.pipeline.width,
          render: (_value, row) => <PipelineCellContent row={row} />,
        },
        updated_at: {
          dataIndex: "updatedAtLabel",
          ellipsis: true,
          key: "updated_at",
          title: (
            <SortableHeaderCell
              columnKey="updated_at"
              label={PROJECT_TABLE_COLUMN_DEFINITIONS.updated_at.label}
              onSort={onSort}
              sortConfig={sortConfig}
            />
          ),
          width: PROJECT_TABLE_COLUMN_DEFINITIONS.updated_at.width,
          render: (_value, row) => (
            <Tooltip title={row.updatedAtTitle}>
              <CellText>{row.updatedAtLabel}</CellText>
            </Tooltip>
          ),
        },
        actions: {
          align: "right",
          dataIndex: "project",
          key: "actions",
          title: renderHeader({
            column: PROJECT_TABLE_COLUMN_DEFINITIONS.actions,
          }),
          width: PROJECT_TABLE_COLUMN_DEFINITIONS.actions.width,
          render: (_value, row) => {
            const { canManageCollaborators, isCollaborator } = getRaasProjectCollaboration({
              currentUser, project: row.project,
            });
            return (
            <ActionCell
              onClick={(event) => event.stopPropagation()}
              onKeyDown={(event) => event.stopPropagation()}
            >
              <Dropdown
                menu={{
                  items: [
                    {
                      key: "edit",
                      label: (
                        <MenuItemContent>
                          <Icon icon="edit" $size={18} />
                          Edit
                        </MenuItemContent>
                      ),
                    },
                    {
                      key: "share",
                      label: (
                        <MenuItemContent>
                          <Icon icon="person_add" $size={18} />
                          Share Project
                        </MenuItemContent>
                      ),
                    },
                    {
                      key: "delete",
                      label: (
                        <MenuItemContent>
                          <Icon icon="delete" $size={18} />
                          Delete
                        </MenuItemContent>
                      ),
                    },
                  ].filter((item) =>
                    (item.key !== "delete" || canManageCollaborators) &&
                    (item.key !== "share" || (portalId === RaasPortalId.Client && (canManageCollaborators || isCollaborator))),
                  ),
                  onClick: ({ domEvent, key }) => {
                    domEvent.stopPropagation();
                    if (key === "edit") {
                      onEditProject({ project: row.project });
                      return;
                    }
                    if (key === "share" && portalId === RaasPortalId.Client && (canManageCollaborators || isCollaborator)) {
                      onShareProject({ project: row.project });
                      return;
                    }
                    if (key === "delete" && canManageCollaborators) {
                      onDeleteProject({ project: row.project });
                    }
                  },
                }}
                trigger={["click"]}
              >
                <IconButton
                  aria-label={`Project actions for ${row.projectNameLabel}`}
                  onClick={(event) => event.stopPropagation()}
                  type="button"
                >
                  <Icon icon="more_vert" $size={18} />
                </IconButton>
              </Dropdown>
            </ActionCell>
            );
          },
        },
      };

      return columnKeys.map((columnKey) => columnsByKey[columnKey]);
    },
    [portalId, columnKeys, companyDetailsByMetaRef, currentUser, onDeleteProject, onEditProject, onShareProject, onSort, sortConfig],
  );
};
